/*
 * Copyright © 2020 FullDigits
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software without restriction, including without limitation the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:
 * The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 *
 * @file Main.ts
 * @author tokiniaina
 */
import gsap from 'gsap'
import { CSSPlugin } from 'gsap/CSSPlugin'
import { ScrollToPlugin } from 'gsap/ScrollToPlugin'
import UIkit from 'uikit'
import { CountUp } from 'countup.js'

import ComponentsRouter from '@script/router/ComponentsRouter'
import header from '@script/components/header'
import videoElement from '@script/components/video'
import tabSwitcher from '@script/components/switcher'
import popinCarousel from '@script/components/popin-carousel'
import popinImage from '@script/components/popin-image'

gsap.registerPlugin(CSSPlugin, ScrollToPlugin)

export default class Main {
    /**
     * Constructor
     */
    constructor (app) {
        this.$app = app
        this.$header = header
        this.$components = {
            tabSwitcher,
            videoElement,
            popinCarousel,
            popinImage
        }
    }

    /**
     * Init Main
     * @void
     */
    init () {
        let timeline = gsap.timeline(
            {
                delay: 0.5,
                paused: true,
                onComplete: () => this.setup()
            }
        )
        let subMenu = document.querySelectorAll('.sub-menu > li > a')

        timeline
            .fromTo(
                document.querySelectorAll('.section-hero .nav-hero a'),
                { autoAlpha: 0 },
                {
                    duration: 1,
                    autoAlpha: 1,
                    ease: 'power2.out',
                    clearProps: 'all'
                },
                1.5
            )

        if (this.$app instanceof HTMLElement && !this.$app.classList.contains('page-home')) {
            timeline.fromTo(
                [
                    document.querySelector('.section-hero .hour'),
                    document.querySelector('.section-hero .title')
                ],
                {
                    autoAlpha: 0,
                    y: 100
                },
                {
                    duration: 1,
                    autoAlpha: 1,
                    y: 0,
                    ease: 'power2.out',
                    stagger: 0.2,
                    clearProps: 'all'
                },
                1
            )
        }

        subMenu.forEach(
            link => {
                link.addEventListener(
                    'click',
                    event => {
                        this.smoothScroll(event.target.hash)

                        event.preventDefault()

                        return false
                    }
                )
            }
        )

        timeline.play()

        // init Header
        this.$header.init()
    }

    /**
     * Setup application
     * @param complete
     */
    setup (complete) {
        const COMPONENT = new ComponentsRouter(this.$components)

        COMPONENT.load()

        this.lazyloadProcess()
        this.componentsBehavior()

        if (window.location.hash) {
            this.smoothScroll(window.location.hash)
        }

        if (complete) {
            complete()
        }
    }

    /**
     * Image lazyload
     * fallback for modern browser lazyload https://web.dev/lazy-loading-images/
     * @void
     */
    lazyloadProcess () {
        const MEDIAS = document.querySelectorAll('.media-element:not(.loaded)')

        if ('IntersectionObserver' in window) {
            let _imageObserver = new IntersectionObserver(
                entries => {
                    entries.forEach(
                        entry => {
                            if (entry.isIntersecting) {
                                let _image = entry.target.querySelector('img')

                                entry.target.classList.add('loaded')
                                _imageObserver.unobserve(_image)
                            }
                        }
                    )
                })

            MEDIAS.forEach(
                media => {
                    _imageObserver.observe(media)
                }
            )
        }
    }

    /**
     * Set animation on scrollspy
     */
    componentsBehavior () {
        // Page top trigger
        UIkit.util.on(
            '#page-top-trigger',
            'outview',
            () => {
                document.body.classList.add('stick-menu')
            }
        )

        UIkit.util.on(
            '#page-top-trigger',
            'inview',
            () => {
                document.body.classList.remove('stick-menu')
            }
        )

        // Number
        UIkit.scrollspy(
            '.number',
            {
                delay: 80,
                offsetTop: -20
            }
        )

        UIkit.util.on(
            '.number',
            'inview',
            event => {
                let options = {
                    separator: ' ',
                    duration: 2
                }
                let number = event.target.innerText.replace(/\s/g, '')
                let countUp = new CountUp(event.target, number, options)

                countUp.start()
            }
        )

        // Main animation
        UIkit.scrollspy(
            'main',
            {
                target: '.animated',
                cls: 'animated-active',
                delay: 80,
                offsetTop: -20
            }
        )
    }

    /**
     * Anchor smooth scroll
     * @param hash
     */
    smoothScroll (hash) {
        let offset = this.$header.getoffsetHeight()
        const media = document.querySelector('.application')

        gsap.to(
            media,
            {
                duration: 1,
                scrollTo: {
                    y: hash,
                    offsetY: offset + 20,
                    ease: 'power4.easeInOut'
                }
            }
        )
    }
}
